import { fetchMe, type PublicUser } from './api.js';

/**
 * Tiny pub-sub for the current user. Screens subscribe and re-render when
 * the auth state changes (login, logout, profile update).
 */
class AuthState {
  private _user: PublicUser | null = null;
  private listeners = new Set<(u: PublicUser | null) => void>();

  get user(): PublicUser | null {
    return this._user;
  }

  set user(value: PublicUser | null) {
    this._user = value;
    for (const fn of this.listeners) fn(value);
  }

  subscribe(fn: (u: PublicUser | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Pull the latest /me from the server. Called on app boot. Notifies
   *  subscribers (so the navbar / guards re-evaluate). */
  async refresh(): Promise<void> {
    this.user = await fetchMe();
  }

  /** Same as refresh() but does NOT notify subscribers — used by the
   *  router on every navigation to keep stats fresh WITHOUT triggering
   *  the `auth.subscribe(() => dispatch())` listener (which would
   *  re-enter dispatch() in a loop and interrupt the route render that
   *  started the refresh in the first place — this is what broke the
   *  "create public/private/ranked game" + tutorial buttons). The
   *  destination route reads `auth.user` at render time, so the fresh
   *  data is still picked up. */
  async refreshSilent(): Promise<void> {
    this._user = await fetchMe();
  }
}

export const auth = new AuthState();
